'use client';

import { c as _c } from "react/compiler-runtime";
import { type CryptoDepositOperation, type CryptoDepositRoute, cryptoDepositArtwork, type OnrampClient, unitsToDecimal } from '@morfi/onramp-sdk';
import { useCryptoDeposit } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ChangeEvent, type ReactNode, useState } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Copy01Icon } from '@/components/ui/icon/glyphs';
import { logger } from '@/lib/telemetry/logger';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingCryptoStatus } from './funding-crypto-status';
import './funding-crypto-deposit.css';
export function FundingCryptoDeposit(t0) {
  const $ = _c(40);
  const {
    client,
    active,
    operationId,
    onBack,
    onComplete
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  let t1;
  if ($[0] !== operationId) {
    t1 = operationId === undefined ? {} : {
      operationId
    };
    $[0] = operationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== active || $[3] !== locale || $[4] !== onComplete || $[5] !== t1) {
    t2 = {
      surface: "web",
      locale,
      active,
      onComplete,
      ...t1
    };
    $[2] = active;
    $[3] = locale;
    $[4] = onComplete;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    state,
    actions
  } = useCryptoDeposit(client, t2);
  let t3;
  if ($[7] !== t) {
    t3 = t("crypto_deposit_title");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useFundingStage(t3, false, onBack);
  if (!state.operation) {
    if (state.error) {
      let t4;
      if ($[9] !== t) {
        t4 = t("crypto_deposit_unavailable");
        $[9] = t;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      let t5;
      if ($[11] !== t4) {
        t5 = <p className="text-body text-foreground-secondary">{t4}</p>;
        $[11] = t4;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      const t6 = state.busy;
      let t7;
      if ($[13] !== actions) {
        t7 = () => {
          actions.retry();
        };
        $[13] = actions;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      let t8;
      if ($[15] !== t) {
        t8 = t("retry");
        $[15] = t;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      let t9;
      if ($[17] !== state.busy || $[18] !== t7 || $[19] !== t8) {
        t9 = <AnimatedButton variant="secondary" disabled={t6} onClick={t7}>{t8}</AnimatedButton>;
        $[17] = state.busy;
        $[18] = t7;
        $[19] = t8;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      let t10;
      if ($[21] !== t5 || $[22] !== t9) {
        t10 = <div role="alert" className="flex flex-col gap-3">{t5}{t9}</div>;
        $[21] = t5;
        $[22] = t9;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      return t10;
    }
    let t4;
    if ($[24] !== t) {
      t4 = t("crypto_deposit_loading");
      $[24] = t;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    let t5;
    if ($[26] !== t4) {
      t5 = <p className="text-body text-foreground-secondary">{t4}</p>;
      $[26] = t4;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    return t5;
  }
  if (!state.operation.instructions) {
    let t4;
    if ($[28] !== actions || $[29] !== state.operation || $[30] !== state.selectedRoute) {
      t4 = <CryptoSource operation={state.operation} route={state.selectedRoute} actions={actions} />;
      $[28] = actions;
      $[29] = state.operation;
      $[30] = state.selectedRoute;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    return t4;
  }
  if (!["deposit_address", "checking_transfer"].includes(state.operation.state)) {
    let t4;
    if ($[32] !== actions || $[33] !== state.operation) {
      t4 = <FundingCryptoStatus operation={state.operation} actions={actions} />;
      $[32] = actions;
      $[33] = state.operation;
      $[34] = t4;
    } else {
      t4 = $[34];
    }
    return t4;
  }
  let t4;
  if ($[35] !== actions || $[36] !== state.checkedWithoutTransfer || $[37] !== state.checking || $[38] !== state.operation) {
    t4 = <CryptoInstructions operation={state.operation} checking={state.checking} checkedWithoutTransfer={state.checkedWithoutTransfer} actions={actions} />;
    $[35] = actions;
    $[36] = state.checkedWithoutTransfer;
    $[37] = state.checking;
    $[38] = state.operation;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  return t4;
}
function CryptoSource(t0) {
  const $ = _c(91);
  const {
    operation,
    route,
    actions
  } = t0;
  const t = useTranslations("onramp");
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== actions || $[1] !== operation.routes || $[2] !== route || $[3] !== t) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const assets = unique(operation.routes, _temp);
      const networks = route ? operation.routes.filter(candidate_0 => candidate_0.asset === route.asset) : [];
      if (!route) {
        let t12;
        if ($[15] !== t) {
          t12 = t("crypto_deposit_unavailable");
          $[15] = t;
          $[16] = t12;
        } else {
          t12 = $[16];
        }
        let t13;
        if ($[17] !== t12) {
          t13 = <p role="alert" className="text-body text-foreground-secondary">{t12}</p>;
          $[17] = t12;
          $[18] = t13;
        } else {
          t13 = $[18];
        }
        t11 = t13;
        break bb0;
      }
      let t12;
      if ($[19] !== route) {
        t12 = cryptoDepositArtwork(route);
        $[19] = route;
        $[20] = t12;
      } else {
        t12 = $[20];
      }
      const artwork = t12;
      t9 = "flex flex-col gap-4";
      let t13;
      if ($[21] !== t) {
        t13 = t("crypto_deposit_asset");
        $[21] = t;
        $[22] = t13;
      } else {
        t13 = $[22];
      }
      const t14 = `/assets/onramp/${artwork.asset}`;
      let t15;
      if ($[23] !== t14) {
        t15 = <CryptoArtwork src={t14} />;
        $[23] = t14;
        $[24] = t15;
      } else {
        t15 = $[24];
      }
      let t16;
      if ($[25] !== t) {
        t16 = t("crypto_deposit_asset");
        $[25] = t;
        $[26] = t16;
      } else {
        t16 = $[26];
      }
      const t17 = route.asset;
      let t18;
      if ($[27] !== actions || $[28] !== operation.routes) {
        t18 = event => {
          const first = operation.routes.find(candidate_1 => candidate_1.asset === event.target.value);
          if (first) {
            actions.select(first.id);
          }
        };
        $[27] = actions;
        $[28] = operation.routes;
        $[29] = t18;
      } else {
        t18 = $[29];
      }
      const t19 = assets.map(_temp2);
      let t20;
      if ($[30] !== route.asset || $[31] !== t16 || $[32] !== t18 || $[33] !== t19) {
        t20 = <select aria-label={t16} value={t17} onChange={t18}>{t19}</select>;
        $[30] = route.asset;
        $[31] = t16;
        $[32] = t18;
        $[33] = t19;
        $[34] = t20;
      } else {
        t20 = $[34];
      }
      let t21;
      if ($[35] !== t15 || $[36] !== t20) {
        t21 = <span className="crypto-deposit-select">{t15}{t20}</span>;
        $[35] = t15;
        $[36] = t20;
        $[37] = t21;
      } else {
        t21 = $[37];
      }
      if ($[38] !== t13 || $[39] !== t21) {
        t10 = <label className="flex flex-col gap-2 text-caption text-foreground-secondary">{t13}{t21}</label>;
        $[38] = t13;
        $[39] = t21;
        $[40] = t10;
      } else {
        t10 = $[40];
      }
      t7 = "flex flex-col gap-2 text-caption text-foreground-secondary";
      if ($[41] !== t) {
        t8 = t("crypto_deposit_network");
        $[41] = t;
        $[42] = t8;
      } else {
        t8 = $[42];
      }
      t5 = "crypto-deposit-select";
      const t22 = `/assets/onramp/${artwork.network}`;
      if ($[43] !== t22) {
        t6 = <CryptoArtwork src={t22} />;
        $[43] = t22;
        $[44] = t6;
      } else {
        t6 = $[44];
      }
      if ($[45] !== t) {
        t1 = t("crypto_deposit_network");
        $[45] = t;
        $[46] = t1;
      } else {
        t1 = $[46];
      }
      t2 = route.id;
      if ($[47] !== actions) {
        t3 = event_0 => actions.select(event_0.target.value);
        $[47] = actions;
        $[48] = t3;
      } else {
        t3 = $[48];
      }
      t4 = networks.map(_temp3);
    }
    $[0] = actions;
    $[1] = operation.routes;
    $[2] = route;
    $[3] = t;
    $[4] = t1;
    $[5] = t10;
    $[6] = t11;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t1 = $[4];
    t10 = $[5];
    t11 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[49] !== t1 || $[50] !== t2 || $[51] !== t3 || $[52] !== t4) {
    t12 = <select aria-label={t1} value={t2} onChange={t3}>{t4}</select>;
    $[49] = t1;
    $[50] = t2;
    $[51] = t3;
    $[52] = t4;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== t12 || $[55] !== t5 || $[56] !== t6) {
    t13 = <span className={t5}>{t6}{t12}</span>;
    $[54] = t12;
    $[55] = t5;
    $[56] = t6;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t13 || $[59] !== t7 || $[60] !== t8) {
    t14 = <label className={t7}>{t8}{t13}</label>;
    $[58] = t13;
    $[59] = t7;
    $[60] = t8;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== t) {
    t15 = t("crypto_deposit_receive");
    $[62] = t;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t15) {
    t16 = <SummaryRow label={t15} value="USDC on Solana" />;
    $[64] = t15;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== t) {
    t17 = t("destination");
    $[66] = t;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  let t18;
  if ($[68] !== t) {
    t18 = t("crypto_deposit_destination");
    $[68] = t;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  let t19;
  if ($[70] !== t17 || $[71] !== t18) {
    t19 = <SummaryRow label={t17} value={t18} />;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  let t20;
  if ($[73] !== actions) {
    t20 = stateBusy(actions);
    $[73] = actions;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== actions) {
    t21 = () => {
      actions.continue();
    };
    $[75] = actions;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== t) {
    t22 = t("continue");
    $[77] = t;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  let t23;
  if ($[79] !== t20 || $[80] !== t21 || $[81] !== t22) {
    t23 = <AnimatedButton className="h-11 w-full" disabled={t20} onClick={t21}>{t22}</AnimatedButton>;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <PoweredBy />;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  let t25;
  if ($[84] !== t10 || $[85] !== t14 || $[86] !== t16 || $[87] !== t19 || $[88] !== t23 || $[89] !== t9) {
    t25 = <div className={t9}>{t10}{t14}{t16}{t19}{t23}{t24}</div>;
    $[84] = t10;
    $[85] = t14;
    $[86] = t16;
    $[87] = t19;
    $[88] = t23;
    $[89] = t9;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  return t25;
}
function _temp3(candidate_3) {
  return <option key={candidate_3.id} value={candidate_3.id}>{candidate_3.networkName}</option>;
}
function _temp2(candidate_2) {
  return <option key={candidate_2.asset} value={candidate_2.asset}>{candidate_2.assetName} · {candidate_2.asset}</option>;
}
function _temp(candidate) {
  return candidate.asset;
}
function CryptoInstructions(t0) {
  const $ = _c(67);
  const {
    operation,
    checking,
    checkedWithoutTransfer,
    actions
  } = t0;
  const t = useTranslations("onramp");
  const instructions = operation.instructions;
  const [copied, setCopied] = useState(false);
  if (!instructions) {
    return null;
  }
  const address = instructions.address;
  const route = instructions.route;
  let t1;
  if ($[0] !== address) {
    t1 = async function copy() {
      try {
        await navigator.clipboard.writeText(address);
        setCopied(true);
      } catch {
        logger.warn("onramp.crypto_deposit_copy_failed");
      }
    };
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copy = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("crypto_deposit_send");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${route.asset} · ${route.networkName}`;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <SummaryRow label={t2} value={t3} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("crypto_deposit_receive");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("crypto_deposit_destination");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `USDC · Solana · ${t6}`;
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <SummaryRow label={t5} value={t7} />;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t10;
  let t9;
  if ($[14] !== address) {
    t9 = <div className="funding-crypto-qr relative flex min-h-68 items-center justify-center overflow-hidden rounded-control py-6"><div className="overflow-hidden rounded-control bg-white shadow-lg"><QRCode value={address} size={184} quietZone={12} ecLevel="H" fgColor="black" bgColor="white" logoImage="/assets/onramp/morfi-qr-mark.svg" logoWidth={30} logoHeight={30} logoPadding={6} logoPaddingStyle="circle" removeQrCodeBehindLogo={true} /></div></div>;
    t10 = <code className="break-all text-center font-mono text-xs">{address}</code>;
    $[14] = address;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[17] !== copy) {
    t11 = () => {
      copy();
    };
    $[17] = copy;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== copied || $[20] !== t) {
    t12 = copied ? t("crypto_deposit_copied") : t("manual_transfer_copy");
    $[19] = copied;
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = <AnimatedButton variant="outline" prefix={Copy01Icon} className="h-11 w-full" onClick={t11}>{t12}</AnimatedButton>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== instructions.memo || $[26] !== t) {
    t14 = instructions.memo ? <SummaryRow label={t("crypto_deposit_memo")} value={instructions.memo} /> : null;
    $[25] = instructions.memo;
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== route.asset || $[29] !== route.decimals || $[30] !== route.minimumUnits || $[31] !== route.minimumUsd || $[32] !== t) {
    t15 = route.minimumUnits !== null ? <p className="text-caption text-foreground-secondary">{t("crypto_deposit_minimum", {
        amount: unitsToDecimal(BigInt(route.minimumUnits), route.decimals),
        asset: route.asset
      })}</p> : route.minimumUsd !== null ? <p className="text-caption text-foreground-secondary">{t("crypto_deposit_minimum_usd", {
        amount: route.minimumUsd
      })}</p> : null;
    $[28] = route.asset;
    $[29] = route.decimals;
    $[30] = route.minimumUnits;
    $[31] = route.minimumUsd;
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== route.feeBps || $[35] !== t) {
    t16 = route.feeBps !== null ? <p className="text-caption text-foreground-secondary">{t("crypto_deposit_fee", {
        bps: route.feeBps
      })}</p> : null;
    $[34] = route.feeBps;
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("crypto_deposit_warning");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17) {
    t18 = <p className="text-center text-xs text-foreground-secondary">{t17}</p>;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== checkedWithoutTransfer || $[42] !== checking || $[43] !== operation.state || $[44] !== t) {
    t19 = operation.state !== "deposit_address" || checking || checkedWithoutTransfer ? <output className="rounded-control bg-background-tertiary p-3 text-center text-caption">{statusCopy(t, operation.state, checking || checkedWithoutTransfer)}</output> : null;
    $[41] = checkedWithoutTransfer;
    $[42] = checking;
    $[43] = operation.state;
    $[44] = t;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== actions) {
    t20 = () => {
      actions.reconcile();
    };
    $[46] = actions;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t) {
    t21 = t("crypto_deposit_transferred");
    $[48] = t;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== checking || $[51] !== t20 || $[52] !== t21) {
    t22 = <AnimatedButton className="h-11 w-full" disabled={checking} onClick={t20}>{t21}</AnimatedButton>;
    $[50] = checking;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <PoweredBy />;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t10 || $[56] !== t13 || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t18 || $[61] !== t19 || $[62] !== t22 || $[63] !== t4 || $[64] !== t8 || $[65] !== t9) {
    t24 = <div className="flex flex-col gap-4">{t4}{t8}{t9}{t10}{t13}{t14}{t15}{t16}{t18}{t19}{t22}{t23}</div>;
    $[55] = t10;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
    $[62] = t22;
    $[63] = t4;
    $[64] = t8;
    $[65] = t9;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  return t24;
}
function SummaryRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption text-foreground-secondary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <strong className="text-right text-body">{value}</strong>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between gap-4 rounded-control bg-background-tertiary p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function PoweredBy() {
  const $ = _c(7);
  const t = useTranslations("onramp");
  let t0;
  if ($[0] !== t) {
    t0 = t("powered_by");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span>{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className="h-4 w-16 bg-transparent" src="/assets/onramp/moonpay.svg" alt="MoonPay" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = <div className="flex items-center justify-center gap-2 text-xs text-foreground-secondary">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function CryptoArtwork(t0) {
  const $ = _c(2);
  const {
    src
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className="size-10 bg-transparent" src={src} alt="" />;
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function unique<T>(values: readonly T[], key: (value: T) => string): T[] {
  return [...new Map(values.map(value => [key(value), value])).values()];
}
function stateBusy(actions: ReturnType<typeof useCryptoDeposit>['actions']): boolean {
  return actions.getSnapshot().busy;
}
function statusCopy(t: ReturnType<typeof useTranslations<'onramp'>>, state: CryptoDepositOperation['state'], checking: boolean): string {
  if (checking) return t('crypto_deposit_not_found');
  switch (state) {
    case 'source':
      return t('crypto_deposit_state_source');
    case 'deposit_address':
      return t('crypto_deposit_state_deposit_address');
    case 'checking_transfer':
      return t('crypto_deposit_state_checking_transfer');
    case 'source_detected':
      return t('crypto_deposit_state_source_detected');
    case 'below_minimum':
      return t('crypto_deposit_state_below_minimum');
    case 'review_hold':
      return t('crypto_deposit_state_review_hold');
    case 'processing':
      return t('crypto_deposit_state_processing');
    case 'complete':
      return t('crypto_deposit_state_complete');
    case 'instructions_expired':
      return t('crypto_deposit_state_instructions_expired');
    case 'late_transfer':
      return t('crypto_deposit_state_late_transfer');
    case 'multiple_transfers':
      return t('crypto_deposit_state_multiple_transfers');
    case 'route_failed':
      return t('crypto_deposit_state_route_failed');
    case 'refund':
      return t('crypto_deposit_state_refund');
  }
}